import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowRight, Newspaper, AlertCircle, ClipboardCheck, Mail, Lock } from "lucide-react";
import { SITE, RESOURCES } from "@/lib/site";
import PageHero, { ScheduleConsultationButton } from "@/components/site/PageHero";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { requestResourceAccess } from "@/lib/api";

const TABS = [
  { key: "articles", label: "Articles", icon: Newspaper, items: RESOURCES.articles },
  { key: "tax-updates", label: "Tax Updates", icon: AlertCircle, items: RESOURCES["tax-updates"] },
  { key: "checklists", label: "Business Checklists", icon: ClipboardCheck, items: RESOURCES.checklists },
];

export default function Resources() {
  const navigate = useNavigate();
  const { section } = useParams();
  const initial = TABS.find((t) => t.key === section)?.key || "articles";

  const [tab, setTab] = useState(initial);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ email: "", name: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const next = TABS.find((t) => t.key === section)?.key;
    if (next && next !== tab) setTab(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section]);

  function onTabChange(v) {
    setTab(v);
    navigate(`/resources/${v}`, { replace: true });
  }

  function onSelect(item, resourceType) {
    setSelected({ title: item.title, type: resourceType, fileUrl: item.fileUrl });
    setOpen(true);
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (!form.email) return;
    try {
      setLoading(true);
      await requestResourceAccess({
        email: form.email,
        name: form.name || undefined,
        resource_type: selected.type,
        resource_title: selected.title,
        file_url: selected.fileUrl || undefined,
      });
      if (selected.fileUrl) {
        // Trigger download in a new tab/window
        window.open(selected.fileUrl, "_blank", "noopener,noreferrer");
        toast.success("Download started.", { description: `Sent to ${form.email} as well.` });
      } else {
        toast.success("Access requested.", { description: "Check your inbox — we've sent a confirmation." });
      }
      setOpen(false);
      setForm({ email: "", name: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div data-testid="resources-page">
      <PageHero
        eyebrow="Resource Center"
        title="Insights, updates, and tools to keep your organization moving forward."
        subtitle="Browse articles, recent tax updates, and business checklists — curated by Tina Jacobs and the TRJ Services team."
        image={SITE.assets.officeModern}
      >
        <a href="#resource-tabs" className="trj-btn-green" data-testid="resources-hero-browse-btn">
          Browse Resources <ArrowRight className="w-4 h-4" />
        </a>
        <ScheduleConsultationButton variant="gold" testid="resources-hero-schedule-btn" />
      </PageHero>

      <section id="resource-tabs" className="bg-white py-20 md:py-24 scroll-mt-28" data-testid="resources-content">
        <div className="max-w-7xl mx-auto px-6">
          <Tabs value={tab} onValueChange={onTabChange}>
            <TabsList
              data-testid="resource-tabs-list"
              className="bg-[var(--trj-light)] p-1 rounded-none border border-gray-200 inline-flex h-auto flex-wrap"
            >
              {TABS.map((t) => (
                <TabsTrigger
                  key={t.key}
                  value={t.key}
                  data-testid={`resource-tab-${t.key}`}
                  className="rounded-none data-[state=active]:bg-[var(--trj-navy)] data-[state=active]:text-white px-5 py-2.5 text-sm font-medium flex items-center gap-2"
                >
                  <t.icon className="w-4 h-4" /> {t.label}
                </TabsTrigger>
              ))}
            </TabsList>

            {TABS.map((t) => (
              <TabsContent key={t.key} value={t.key} className="mt-10" id={t.key}>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {t.items.map((item, idx) => (
                    <article
                      key={item.title}
                      data-testid={`resource-item-${t.key}-${idx}`}
                      className="trj-card flex flex-col h-full"
                    >
                      <div className="flex items-center justify-between">
                        <div className="trj-eyebrow !text-[var(--trj-green)]">{t.label}</div>
                        {item.comingSoon ? (
                          <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 bg-[var(--trj-gold)]/15 text-[var(--trj-gold-dark)]" data-testid={`resource-coming-soon-${t.key}-${idx}`}>
                            Coming Soon
                          </span>
                        ) : item.fileUrl ? (
                          <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 bg-[var(--trj-green)]/15 text-[var(--trj-green-dark)]">
                            Available
                          </span>
                        ) : (
                          <Lock className="w-3.5 h-3.5 text-[var(--trj-muted)]" />
                        )}
                      </div>
                      <h3 className="font-serif-trj text-xl text-[var(--trj-navy)] mt-3 leading-snug">{item.title}</h3>
                      <p className="mt-3 text-sm text-[var(--trj-muted)] leading-relaxed">{item.description}</p>
                      <button
                        onClick={() => !item.comingSoon && onSelect(item, t.key)}
                        disabled={item.comingSoon}
                        className={`text-sm mt-6 self-start ${item.comingSoon ? "trj-btn-outline-navy opacity-50 cursor-not-allowed" : "trj-btn-outline-navy"}`}
                        data-testid={`resource-access-btn-${t.key}-${idx}`}
                      >
                        {item.comingSoon
                          ? "Notify me when available"
                          : t.key === "downloads"
                            ? "Get Download"
                            : "Read More"} <ArrowRight className="w-4 h-4" />
                      </button>
                    </article>
                  ))}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[var(--trj-light)] py-20 text-center" data-testid="resources-cta">
        <div className="max-w-3xl mx-auto px-6">
          <div className="trj-eyebrow mb-3">Need Personal Guidance?</div>
          <h2 className="font-serif-trj text-3xl md:text-4xl text-[var(--trj-navy)] leading-tight">
            Schedule a consultation with TRJ Services.
          </h2>
          <div className="mt-8 flex justify-center flex-wrap gap-3">
            <ScheduleConsultationButton testid="resources-cta-schedule-btn" />
            <Link to="/contact" className="trj-btn-outline-navy" data-testid="resources-cta-contact-btn">
              Contact Us
            </Link>
          </div>
        </div>
      </section>

      {/* EMAIL GATE DIALOG */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="resource-email-dialog" className="sm:max-w-md rounded-none border-t-4 border-[var(--trj-gold)]">
          <DialogHeader>
            <div className="trj-eyebrow !text-[var(--trj-green)] mb-1">Resource Access</div>
            <DialogTitle className="font-serif-trj text-2xl text-[var(--trj-navy)]">{selected?.title}</DialogTitle>
            <DialogDescription className="text-[var(--trj-muted)]">
              Enter your email and we&apos;ll send your access confirmation. Your information is kept private.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <Label htmlFor="resource-name" className="text-xs uppercase tracking-widest text-[var(--trj-muted)]">Name (optional)</Label>
              <Input
                id="resource-name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className="rounded-none mt-1"
                data-testid="resource-form-name"
                placeholder="Your name"
              />
            </div>
            <div>
              <Label htmlFor="resource-email" className="text-xs uppercase tracking-widest text-[var(--trj-muted)]">Email *</Label>
              <Input
                id="resource-email"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                className="rounded-none mt-1"
                data-testid="resource-form-email"
                placeholder="you@example.com"
              />
            </div>
            <DialogFooter className="gap-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="trj-btn-outline-navy text-sm"
                data-testid="resource-form-cancel"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="trj-btn-green text-sm disabled:opacity-60"
                data-testid="resource-form-submit"
              >
                <Mail className="w-4 h-4" /> {loading ? "Sending…" : "Send Access"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
